import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, MessagesSquare, PenLine, X } from 'lucide-react';

function CreateBookModal({ onClose, onCreate }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState('');
  const [genre, setGenre] = useState('');
  const [creating, setCreating] = useState(false);

  const GENRES = [
    { value: 'memoir', label: t('genres.memoir'), description: t('genres.memoirDesc') },
    { value: 'lifestory', label: t('createBook.lifeStory', 'Life Story'), description: t('createBook.lifeStoryDesc', 'Chronicle your journey') },
    { value: 'fiction', label: t('genres.fiction'), description: t('genres.fictionDesc') },
    { value: 'shortstory', label: t('createBook.shortStory', 'Short Story'), description: t('createBook.shortStoryDesc', 'Compact, impactful tales') },
    { value: 'business', label: t('genres.business'), description: t('genres.businessDesc') },
    { value: 'childrens', label: t('genres.childrens'), description: t('genres.childrensDesc') },
    { value: 'freestyle', label: t('genres.freestyle'), description: t('genres.freestyleDesc') },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!genre) return;

    setCreating(true);
    const isInterviewMode = mode === 'interview';
    await onCreate({
      title: 'Untitled Book',
      genre,
      description: '',
      is_interview_mode: isInterviewMode,
    });
    setCreating(false);
  };

  const selectInterviewMode = () => {
    setMode('interview');
    setGenre('');
  };

  const selectWritingMode = () => {
    setMode('write');
    setGenre('');
  };

  const showModeChoice = !mode;
  const showGenreChoice = mode === 'write' || mode === 'interview';
  const interviewSelected = mode === 'interview';

  return (
    <div className="fixed inset-0 bg-black/45 dark:bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4 z-[100]">
      <div className="bg-[var(--story-bg)] rounded-t-[1.5rem] sm:rounded-[1.25rem] shadow-2xl w-full max-w-lg max-h-[92vh] sm:max-h-[85vh] overflow-hidden flex flex-col border border-[var(--story-line)]">
        <div className="bg-[var(--story-bg)] border-b border-[var(--story-line)] px-5 py-4 flex items-start justify-between flex-shrink-0">
          <div>
            <p className="story-label mb-1">New story</p>
            <h2 className="story-section-title text-xl">{t('modal.createBook')}</h2>
          </div>
          <button onClick={onClose} className="text-[#2A2420]/50 dark:text-[#F0EBE4]/50 hover:text-[#2A2420] dark:hover:text-white p-1.5 -mr-1 rounded-lg hover:bg-[#F0EBE4]/50 dark:hover:bg-[#2A2520]/50 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4] mb-2">
              {showGenreChoice ? t('book.genre') : 'Choose the easiest way in'}
            </label>

            {showGenreChoice && (
              <button
                type="button"
                onClick={() => {
                  setMode('');
                  setGenre('');
                }}
                className="inline-flex items-center gap-1.5 text-xs text-[#8B7355] dark:text-[#C4A882] hover:underline underline-offset-2 mb-3"
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                Back to options
              </button>
            )}

            {showModeChoice && (
              <div className="space-y-3">
                <button
                  type="button"
                  onClick={selectInterviewMode}
                  data-testid="genre-interview"
                  className={`w-full text-left rounded-2xl border transition-all duration-200 overflow-hidden ${
                    interviewSelected
                      ? 'border-[#C4A882] ring-2 ring-[#C4A882]/20 shadow-md'
                      : 'border-[#E0D8CC] dark:border-[#2A2520] hover:border-[#C4A882]/50 hover:shadow-sm'
                  }`}
                >
                  <div className={`relative p-4 transition-all duration-200 ${
                    interviewSelected
                      ? 'bg-gradient-to-br from-[#1E1A14] to-[#2A2016]'
                      : 'bg-gradient-to-br from-[#28231C] to-[#221D18]'
                  }`}>
                    <div className="absolute top-3 right-3">
                      <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide uppercase px-2 py-0.5 rounded-full bg-[#C4A882]/20 text-[#C4A882] border border-[#C4A882]/30">
                        Guided
                      </span>
                    </div>

                    <div className="flex items-start gap-3 pr-20">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
                        interviewSelected ? 'bg-[#C4A882]/25' : 'bg-[#F0EBE4]/10'
                      }`}>
                        <MessagesSquare className={`w-5 h-5 transition-colors ${
                          interviewSelected ? 'text-[#C4A882]' : 'text-[#F0EBE4]/70'
                        }`} />
                      </div>
                      <div>
                        <div className="font-semibold text-[#F0EBE4] text-base leading-tight">
                          {t('createBook.interviewMode', 'Interview Mode')}
                        </div>
                        <div className="text-[#F0EBE4]/60 text-xs mt-0.5 leading-relaxed">
                          {t('createBook.interviewModeDesc', 'AI asks the questions. You answer. We write the chapters.')}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5 mt-3 pl-0.5">
                      {['Guided questions', 'Any topic', 'Auto chapters'].map((tag) => (
                        <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[#F0EBE4]/8 text-[#F0EBE4]/50 border border-[#F0EBE4]/10">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={selectWritingMode}
                  data-testid="mode-write-book"
                  className="story-surface-subtle w-full text-left p-4 transition-colors hover:border-[var(--story-line-strong)]"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#F8F5F0] dark:bg-[#1A1816] flex items-center justify-center">
                      <PenLine className="w-5 h-5 text-[#8B7355] dark:text-[#C4A882]" />
                    </div>
                    <div className="flex-1">
                      <div className="font-semibold text-[#2A2420] dark:text-[#F0EBE4] text-base leading-tight">
                        Write a Book
                      </div>
                      <div className="text-[#2A2420]/55 dark:text-[#F0EBE4]/55 text-xs mt-0.5 leading-relaxed">
                        Start with a blank book and choose the type next.
                      </div>
                    </div>
                    <BookOpen className="w-4 h-4 text-[#8B7355] dark:text-[#C4A882] mt-1" />
                  </div>
                </button>
              </div>
            )}
          </div>

          {showGenreChoice && (
            <div className="-mt-2">
              <p className="text-sm font-medium text-[#2A2420] dark:text-[#F0EBE4]">
                {mode === 'interview' ? 'What kind of book should the interview guide?' : 'What kind of book are you writing?'}
              </p>
              <p className="mt-1 text-xs leading-relaxed text-[#2A2420]/55 dark:text-[#F0EBE4]/55">
                {mode === 'interview'
                  ? 'The questions, chapter arc, and AI writing style will adapt to this choice.'
                  : 'This helps the writing tools shape the right kind of chapters.'}
              </p>
            </div>
          )}

          {showGenreChoice && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 -mt-1">
              {GENRES.map((g) => (
                <button
                  key={g.value}
                  type="button"
                  onClick={() => setGenre(g.value)}
                  data-testid={`genre-${g.value}`}
                  className={`text-left p-3 rounded-xl border transition-all ${
                    genre === g.value
                      ? 'bg-[#F8F5F0] dark:bg-[#2A2520] border-[#8B7355] dark:border-[#C4A882]'
                      : 'bg-white dark:bg-[#2A2520]/60 border-[#E0D8CC] dark:border-[#2A2520] hover:border-[#C4A882]/50 dark:hover:border-[#C4A882]/40'
                  }`}
                >
                  <div className="font-medium text-sm text-[#2A2420] dark:text-[#F0EBE4] leading-tight">{g.label}</div>
                  <div className="text-xs text-[#2A2420]/50 dark:text-[#F0EBE4]/50 mt-1 line-clamp-1 leading-tight">{g.description}</div>
                </button>
              ))}
            </div>
          )}
        </form>

        <div className="bg-[var(--story-bg)] border-t border-[var(--story-line)] px-5 py-4 flex space-x-3 flex-shrink-0">
          <Button
            type="button"
            onClick={onClose}
            variant="outline"
            className="story-button-secondary flex-1 min-h-[44px]"
          >
            {t('modal.cancel')}
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            disabled={!genre || creating}
            data-testid="create-book-submit"
            className="story-button-primary flex-1 min-h-[44px]"
          >
            {creating ? t('modal.creating') : t('createBook.createBook', 'Create Book')}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CreateBookModal;
